import React from 'react';
import { AnimatedValue, UseSpringProps, useSprings } from 'react-spring';

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export interface SliderSpringsProps {
  readonly offsetRef: React.MutableRefObject<number>;
  readonly slideWidth: number;
  readonly sliderWidth: number;
  readonly totalSlideCount: number;
  readonly visibleSlideCount: number;
  readonly immediate?: boolean;
  readonly onStart?: () => void;
  readonly onRest?: () => void;
}

export interface SlideSpringProps {
  readonly x: number;
  readonly opacity: number;
  readonly pointerEvents: 'all' | 'none';
  readonly visibility?: 'hidden' | 'visible';
}

export type SlideSpring = AnimatedValue<SlideSpringProps>;

export function useSliderSprings({
  offsetRef,
  slideWidth,
  sliderWidth,
  totalSlideCount,
  visibleSlideCount,
  immediate,
  onStart,
  onRest,
}: SliderSpringsProps): [SlideSpring[], (relativeDelta?: number) => void] {
  const getSpringProps = React.useCallback(
    (index: number, relativeDelta = 0): UseSpringProps<SlideSpringProps> => {
      const visible = index >= offsetRef.current && index < offsetRef.current + visibleSlideCount;

      const pointerEvents = visible ? 'all' : 'none';

      const visibility = immediate && visibleSlideCount === 1 && !visible ? 'hidden' : 'visible';

      const opacity = visible ? 1 : Math.abs(relativeDelta);
      const baseX = offsetRef.current * slideWidth;
      const x = baseX + relativeDelta * sliderWidth;
      const clampedX = clamp(x, baseX - slideWidth / 2, baseX + slideWidth / 2);

      return {
        from: { x: baseX, opacity: visible ? 1 : 0, pointerEvents, visibility },
        x: clampedX,
        opacity,
        pointerEvents,
        visibility,
        immediate,
        config: { tension: 250 },
        onStart,
        onRest,
      };
    },
    [offsetRef, slideWidth, sliderWidth, visibleSlideCount, immediate]
  );

  const [springs, setSpringProps] = useSprings(totalSlideCount, (index) => getSpringProps(index));

  return [
    springs,
    React.useCallback(
      (relativeDelta) => {
        setSpringProps((index) => getSpringProps(index, relativeDelta));
      },
      [setSpringProps, getSpringProps]
    ),
  ];
}
